import React, {useMemo, useState} from "react";
import {Link, useNavigate} from "react-router-dom";
import {useDispatch} from "react-redux";
import {authActions} from "../store";
import {useTranslation} from "react-i18next";
import { GoogleOutlined} from "@ant-design/icons";
import {Card, Typography, Input, Button, Form, Tooltip} from "antd";

import {getProvidersUrls} from "../utils/auth";
import {PROVIDER} from "../constants/auth";

import {validateName} from "../utils/InputUtils/NameValidate";
import {validateEmail} from "../utils/InputUtils/EmailValidate";
import {validatePassword} from "../utils/InputUtils/PasswordValidate";

import AuthHeader from "../components/Header/AuthHeader";
import ValidatedInput from "../components/Inputs/ValidatedInput";
import PasswordField from "../components/Inputs/PasswordField";
import PrivacyCheckbox from "../components/CheckBoxs/PrivacyCheckbox";
import TooltipWrapper from "../components/Wrappers/TooltipWrapper";
import WelcomeTutorialModal from "../components/modals/WelcomeTutorialModal";
import {LanguageHeader} from "../components/LanguageHeader/index"

import Public_Offer_IdeaSocial_ru from "../files/Public_Offer_IdeaSocial_ru.pdf";
import Public_Offer_IdeaSocial_en from "../files/Public_Offer_IdeaSocial_en.pdf";
import payment_ru from "../files/payment_ru.pdf";
import payment_en from "../files/payment_en.pdf";
import privacy_ru from "../files/Privacy_Policy_IdeaSocial_ru.pdf";
import privacy_en from "../files/Privacy_Policy_IdeaSocial_en.pdf";


const {Title, Text, Link: ALink} = Typography;

const SignupPage = () => {
    const {t, i18n} = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [fullName, setFullName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [lang] = useState(localStorage.getItem("ln") || i18n.language || "en");

    const [nameState, setNameState] = useState("initial");
    const [nameMessage, setNameMessage] = useState("");
    const [emailState, setEmailState] = useState("initial");
    const [emailMessage, setEmailMessage] = useState("");
    const [passwordState, setPasswordState] = useState("initial");
    const [passwordMessage, setPasswordMessage] = useState("");
    const [accepted, setAccepted] = useState(false);
    const [isWelcomeTutorialModalOpen, setIsWelcomeTutorialModalOpen] = useState(false);

    const termsByLang = {
        ru: Public_Offer_IdeaSocial_ru,
        en: Public_Offer_IdeaSocial_en,
        kk: Public_Offer_IdeaSocial_en,
    };

    const privacyByLang = {
        ru: privacy_ru,
        en: privacy_en,
        kk: privacy_en,
    };

    const resolveDocLang = (ln) => (ln || "en").toLowerCase().split("-")[0];

    const handleSubmit = (e) => {
        e.preventDefault();
        const emailValidationError = validateEmail(email);
        setEmailMessage(emailValidationError);
        if (emailValidationError) return;

        dispatch(authActions.register({username: email, password, name: fullName, lang}));
    };

    const onSocialLogin = (key) => {
        window.location.href = getProvidersUrls()[key];
    };

    const showWelcomeTutorialModal  = () => setIsWelcomeTutorialModalOpen(true);

    const handleNameChange = (e) => {
        const newName = e.target.value;
        setFullName(newName);
        if (!newName) {
            setNameState("initial");
            setNameMessage(t("validation.enterName"));
        } else {
            setNameState("typing");
            setNameMessage("");
        }
    };

    const handleNameBlur = () => {
        if (!fullName) {
            setNameState("initial");
            setNameMessage(t("validation.enterName"));
            return;
        }
        const error = validateName(fullName);
        if (error) {
            setNameState("error");
            setNameMessage(t(error));
        } else {
            setNameState("success");
            setNameMessage(t("validation.nameValid"));
        }
    };

    const handleEmailChange = (e) => {
        const newEmail = e.target.value.toLowerCase();
        setEmail(newEmail);
        if (!newEmail) {
            setEmailState("initial");
            setEmailMessage(t("validation.enterEmail"));
        } else {
            setEmailState("typing");
            setEmailMessage("");
        }
    };

    const handleEmailBlur = () => {
        if (!email) {
            setEmailState("initial");
            setEmailMessage(t("validation.enterEmail"));
            return;
        }
        const err = validateEmail(email);
        if (err) {
            setEmailState("error");
            setEmailMessage(t(err));
        } else {
            setEmailState("success");
            setEmailMessage(t("validation.emailValid"));
        }
    };

    const handlePasswordChange = (e) => {
        const v = e.target.value;
        setPassword(v);
        if (!v) {
            setPasswordState("initial");
            setPasswordMessage(t("validation.enterPassword"));
        } else {
            setPasswordState("typing");
            setPasswordMessage("");
        }
    };

    const handlePasswordBlur = () => {
        if (!password) {
            setPasswordState("initial");
            setPasswordMessage(t("validation.enterPassword"));
            return;
        }
        const err = validatePassword(password);
        if (err) {
            setPasswordState("error");
            setPasswordMessage(t(err));
        } else {
            setPasswordState("success");
            setPasswordMessage(t("validation.passwordValid"));
        }
    };

    const isFormValid = useMemo(() => (
        !validateName(fullName) &&
        !validateEmail(email) &&
        !validatePassword(password)
    ), [fullName, email, password]);

    const currentLang = resolveDocLang(i18n.language);

    const submitDisabled = !accepted || !isFormValid;
    const submitHint = !accepted
        ? (t("signup.acceptPolicy") || "Примите политику и правила")
        : (t("validation.fixValid") || "Проверьте поля формы");

    return (
        <>
            <LanguageHeader/>
            <div className="min-h-screen bg-bgPage flex flex-col items-center px-4 pt-10 pb-6">
                <AuthHeader
                    active="signup"
                    signupText={t("signup.signUp")}
                    loginText={t("signin.signIn")}
                    onSignupClick={() => {
                    }}
                    onLoginClick={() => navigate("/sign-in")}
                />
                <Title level={2} className="my-3 !font-bold" style={{color: '#8932F6'}}>
                    {t("signup.signUp")}
                </Title>
                <Text className="text-textWeak mb-5">
                    {t("signup.submitSign")}
                </Text>

                <Card className="w-full max-w-[440px] rounded-lg shadow-card" bordered={false}>
                        <Form layout="vertical" onSubmitCapture={handleSubmit}>
                            {/*Name*/}
                            <ValidatedInput
                                type="text"
                                label={t("signup.fullName")}
                                placeholder="futuresphere"
                                value={fullName}
                                onChange={handleNameChange}
                                onBlur={handleNameBlur}
                                state={nameState}
                                message={nameMessage}
                                name="fullName"
                                autoComplete="name"
                                showSuccess
                            />

                            {/*Email*/}
                            <ValidatedInput
                                label={t("signin.emailLabel")}
                                type="email"
                                placeholder="future@finesse.com"
                                value={email}
                                onChange={handleEmailChange}
                                onBlur={handleEmailBlur}
                                state={emailState}
                                message={emailMessage}
                                name="email"
                                autoComplete="email"
                                showSuccess
                            />

                            <Form.Item
                                label={t("signin.passwordLabel")}
                                validateStatus={passwordState === "error" ? "error" : undefined}
                                help={passwordMessage || undefined}
                            >
                                <PasswordField
                                    value={password}
                                    onChange={handlePasswordChange}
                                    onBlur={handlePasswordBlur}
                                    placeholder={t("signin.inputPassword")}
                                    className={`h-[44px] rounded-[12px] ${passwordState}`}
                                />
                            </Form.Item>

                            <PrivacyCheckbox
                                checked={accepted}
                                onChange={setAccepted}
                                termsPath={termsByLang[currentLang] || termsByLang.en}
                                privacyPath={privacyByLang[currentLang] || privacyByLang.en}
                            />
                            <div className="space-y-4">
                                <TooltipWrapper disabled={submitDisabled}
                                                title={submitHint}>
                                    <Button
                                        htmlType="submit"
                                        type="primary"
                                        size="large"
                                        className="rounded-full h-[44px] bg-brand border-brand"
                                        block
                                        disabled={submitDisabled}
                                    >
                                        {t("signup.continue")}
                                    </Button>
                                </TooltipWrapper>
                                <TooltipWrapper disabled={!accepted}
                                                title={t("signup.acceptPolicy") || "Примите политику и правила"}>
                                    <Button
                                        size="large"
                                        className="rounded-full h-[44px]"
                                        icon={<GoogleOutlined/>}
                                        block
                                        disabled={!accepted}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (!accepted) return;
                                            onSocialLogin(PROVIDER.GOOGLE);
                                        }}>
                                        {t("signin.signInGoogle")}
                                    </Button>
                                </TooltipWrapper>
                            </div>
                        </Form>
                </Card>

                <div className="mt-6 text-center flex flex-col gap-3">
                    <Text>
                        {t("signup.haveAccount")}{" "} <ALink
                        onClick={() => navigate("/sign-in")}>{t("signin.signIn")}</ALink>
                    </Text>

                    <button
                        className="text-sm hover:text-violet-600 font-semibold hover:underline"
                        onClick={showWelcomeTutorialModal}
                    >
                        {t("signin.howThatWorkingBtn")}
                    </button>

                    <Text type="secondary">{t("signup.copyrights")}</Text>
                </div>
            </div>

            <WelcomeTutorialModal
                open={isWelcomeTutorialModalOpen}
                onClose={() => setIsWelcomeTutorialModalOpen(false)}
                onGo={() => navigate("/how-to-work")}
                rememberKey="isHideWelcomeTutorialModal"
            />
        </>
    );
};

export default SignupPage;
